import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  TrendingUp,
  TrendingDown,
  AccountBalance,
  Receipt
} from '@mui/icons-material';
import axios from 'axios';

interface BetDetail {
  betId: number;
  betNumber: string;
  betAmount: number;
  operatorName: string;
  gameType: string;
  isWin: boolean;
  winningTier: string;
  winAmount: number;
  placedAt: string;
}

interface DownlineDetail {
  downlineUserId: number;
  downlineUsername: string;
  downlineFullName: string;
  bets: BetDetail[];
  salesCommissionFromDownline: number;
  hierarchicalEarnedFromDownline: number;
  hierarchicalOwedFromDownline: number;
  netFromDownline: number;
}

interface UserSettlementReport {
  userId: number;
  username: string;
  fullName: string;
  reportDate: string;
  ownBetsCount: number;
  ownBetsAmount: number;
  ownWins: number;
  ownLosses: number;
  ownWinAmount: number;
  downlineDetails: DownlineDetail[];
  totalSalesCommission: number;
  totalHierarchicalEarned: number;
  totalHierarchicalOwed: number;
  netPosition: number;
}

const UserSettlementReport: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [report, setReport] = useState<UserSettlementReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchReport();
  }, [selectedDate]);

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      // Format date as YYYY-MM-DD in local timezone
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;

      const response = await axios.get(`/settlement/my-report/${dateStr}`);
      setReport(response.data);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('No settlement report found for this date. Settlement may not have been processed yet.');
      } else {
        setError(err.response?.data?.message || 'Failed to fetch settlement report');
      }
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return `RM ${amount.toFixed(2)}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h4" component="h1">
              My Settlement Report
            </Typography>
            <DatePicker
              label="Report Date"
              value={selectedDate}
              onChange={(newValue) => newValue && setSelectedDate(newValue)}
              slotProps={{
                textField: { size: 'small', sx: { width: 200 } }
              }}
            />
          </Box>

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {report && !loading && (
            <>
              {/* Summary Cards */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Receipt sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography color="text.secondary" variant="body2">
                          Own Bets
                        </Typography>
                      </Box>
                      <Typography variant="h5">{report.ownBetsCount}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatCurrency(report.ownBetsAmount)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <TrendingUp sx={{ mr: 1, color: 'success.main' }} />
                        <Typography color="text.secondary" variant="body2">
                          Sales Commission
                        </Typography>
                      </Box>
                      <Typography variant="h5" color="success.main">
                        {formatCurrency(report.totalSalesCommission)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        From your bets
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card>
                    <CardContent>
                      <Typography color="text.secondary" variant="body2" sx={{ mb: 1 }}>
                        Hierarchical Commission
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">Earned:</Typography>
                        <Typography variant="body2" color="success.main">
                          {formatCurrency(report.totalHierarchicalEarned)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Owed:</Typography>
                        <Typography variant="body2" color="error.main">
                          {formatCurrency(report.totalHierarchicalOwed)}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <AccountBalance sx={{ mr: 1, color: report.netPosition >= 0 ? 'success.main' : 'error.main' }} />
                        <Typography color="text.secondary" variant="body2">
                          Net Position
                        </Typography>
                      </Box>
                      <Typography 
                        variant="h5" 
                        color={report.netPosition >= 0 ? 'success.main' : 'error.main'}
                      >
                        {formatCurrency(report.netPosition)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {report.ownWins}W / {report.ownLosses}L
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Own Bets Section */}
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  My Bets ({report.ownBetsCount})
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Bet Number</TableCell>
                        <TableCell>Operator</TableCell>
                        <TableCell align="right">Stake</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Win Amount</TableCell>
                        <TableCell>Tier</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {report.downlineDetails.length === 0 && report.ownBetsCount > 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography variant="body2" color="text.secondary">
                              Bet details are available in Transaction History
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : report.ownBetsCount === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography variant="body2" color="text.secondary">
                              No bets placed on this date
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              {/* Downline Commission Breakdown */}
              {report.downlineDetails.length > 0 && (
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Commission from Downlines
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  {report.downlineDetails.map((downline) => (
                    <Box key={downline.downlineUserId} sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                        {downline.downlineFullName} (@{downline.downlineUsername})
                      </Typography>
                      
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid size={{ xs: 6, md: 3 }}>
                          <Typography variant="body2" color="text.secondary">
                            Bets: {downline.bets.length}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                          <Typography variant="body2" color="success.main">
                            Earned: {formatCurrency(downline.hierarchicalEarnedFromDownline)}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                          <Typography variant="body2" color="error.main">
                            Owed: {formatCurrency(downline.hierarchicalOwedFromDownline)}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                          <Typography 
                            variant="body2" 
                            fontWeight="bold"
                            color={downline.netFromDownline >= 0 ? 'success.main' : 'error.main'}
                          >
                            Net: {formatCurrency(downline.netFromDownline)}
                          </Typography>
                        </Grid>
                      </Grid>

                      {downline.bets.length > 0 && (
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Bet Number</TableCell>
                                <TableCell>Operator</TableCell>
                                <TableCell align="right">Stake</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Win Amount</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {downline.bets.map((bet) => (
                                <TableRow key={bet.betId}>
                                  <TableCell>{bet.betNumber}</TableCell>
                                  <TableCell>{bet.operatorName}</TableCell>
                                  <TableCell align="right">{formatCurrency(bet.betAmount)}</TableCell>
                                  <TableCell>
                                    <Chip
                                      label={bet.isWin ? 'WON' : 'LOST'}
                                      color={bet.isWin ? 'success' : 'error'}
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    {bet.isWin ? formatCurrency(bet.winAmount) : '-'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Box>
                  ))}
                </Paper>
              )}
            </>
          )}
        </Paper>
      </Container>
    </LocalizationProvider>
  );
};

export default UserSettlementReport;

